import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProjects } from '../api/queries/projects';
import { useCreateExpense, useExpense } from '../api/queries/expenses';
import { useUploadDocument } from '../api/queries/documents';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { useAssets } from '../api/queries/assets';
import { RECUR_OPTIONS, useCreateRecurringExpense } from '../api/queries/recurring-expenses';
import { ApiError } from '../api/client';
import {
  buildReceiptFilename,
  extensionOf,
  normalizeReceiptFile,
  renameFile,
} from '../utils/receipt';
import { tracksMileage } from '../utils/expense';
import { assetOptionLabel } from '../utils/asset';
import { apiErrorText } from '../utils/apiErrors';
import { fmtDate } from '../utils/date';
import { DateInput } from '../components/DateInput';
import { FileDrop } from '../components/FileDrop';
import { VendorPicker } from '../components/VendorPicker';
import { MoneyInput } from '../components/MoneyInput';
import { ProjectOptions } from '../components/ProjectOptions';
import { MatterSelect } from '../components/MatterSelect';
import { can } from '../auth/store';
import './NewExpensePage.css';
import { Toggle } from '../components/Toggle';

// Sentinel value for the "general business expense" option in the
// project dropdown. Sent to the API as null.
const GENERAL_BUSINESS = -1 as const;

function todayEpoch(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
}

export function NewExpensePage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const create = useCreateExpense();
  const createRecur = useCreateRecurringExpense();
  const uploadDoc = useUploadDocument();
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  // Non-fatal: the receipt uploaded but didn't become a PDF.
  const [receiptNote, setReceiptNote] = useState<string | null>(null);
  // Recurrence: 'none' (default) or an RECUR_OPTIONS value. When set, saving
  // records this expense AND creates a repeating rule for future periods.
  const [repeat, setRepeat] = useState('none');
  const [recurName, setRecurName] = useState('');
  // Guards against re-creating the one-off expense if the follow-up rule
  // creation fails and the user re-submits.
  const [expenseSaved, setExpenseSaved] = useState(false);

  const initialProjectId = (() => {
    const v = searchParams.get('project_id');
    if (!v) return 0;
    if (v === 'none') return GENERAL_BUSINESS;
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? n : 0;
  })();

  // 0 = unselected sentinel, -1 = general business (no project),
  // positive = a real project id.
  const [projectId, setProjectId] = useState<number>(initialProjectId);
  // The project's matter this cost belongs to (0061); ?matter_id= from the
  // matter's row on the project page.
  const [matterId, setMatterId] = useState<number | null>(() => {
    const n = Number(searchParams.get('matter_id'));
    return initialProjectId > 0 && Number.isInteger(n) && n > 0 ? n : null;
  });
  const [type, setType] = useState<string>('Travel');
  // The form captures item subtotal + sales tax separately. The total
  // (what the server stores as `amount`) is computed at submit time.
  const [itemAmount, setItemAmount] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [dateEpoch, setDateEpoch] = useState<number | null>(() => todayEpoch());
  // Vendor is now a structured reference. We track only the FK id; the
  // VendorPicker resolves the display name and (when relevant) pre-fills
  // the tax category from the vendor's default.
  const [vendorId, setVendorId] = useState<number | null>(null);
  // A name-only vendor — a `generic_vendor` category ("Gas station") or a
  // one-off the user typed. Mutually exclusive with vendorId.
  const [vendorText, setVendorText] = useState('');
  // Display name of a PICKED vendor, kept only so the receipt filename can name
  // it — the expense itself carries the vendor by id.
  const [vendorName, setVendorName] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [taxCategory, setTaxCategory] = useState('');
  const [reimbursable, setReimbursable] = useState(false);
  const [mileage, setMileage] = useState('');
  const [notes, setNotes] = useState('');
  const [assetId, setAssetId] = useState<number | null>(null);

  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ---- receipt OCR ----
  // Progress 0..1 while a receipt is being read, null when idle. Never gates
  // Save: the form works exactly as before whether OCR is running, finished or
  // failed.
  const [ocrPct, setOcrPct] = useState<number | null>(null);
  // ---- Duplicate and edit (?from=<expense id>) ----
  // Seeds the form ONCE from an existing expense; nothing is saved until the
  // user saves. Deliberately NOT copied: the date (today — a repeat purchase
  // is a new day), the receipt and receipt # (they belong to one purchase),
  // and the asset link (an asset's cost is the sum of its expenses, so a copy
  // would double it).
  const fromId = (() => {
    const n = Number(searchParams.get('from'));
    return Number.isInteger(n) && n > 0 ? n : null;
  })();
  const sourceQ = useExpense(fromId);
  const seededFrom = useRef<number | null>(null);
  const source = sourceQ.data?.expense;
  useEffect(() => {
    if (!source || seededFrom.current === source.id) return;
    seededFrom.current = source.id;
    setProjectId(source.project_id ?? GENERAL_BUSINESS);
    // The matter is the kind of work, so a copy keeps it.
    setMatterId(source.matter_id ?? null);
    setType(source.type);
    const tax = Number(source.tax_amount ?? 0);
    setItemAmount((Number(source.amount) - tax).toFixed(2));
    setTaxAmount(tax > 0 ? tax.toFixed(2) : '');
    // vendor_id XOR a name-only vendor, exactly as the picker returns them.
    setVendorId(source.vendor_id);
    setVendorName(source.vendor_id !== null ? source.vendor ?? '' : '');
    setVendorText(source.vendor_id === null ? source.vendor ?? '' : '');
    setTaxCategory(source.tax_category ?? '');
    setReimbursable(source.reimbursable);
    setMileage(source.mileage ?? '');
    setNotes(source.notes ?? '');
  }, [source]);

  // Labels of the fields OCR filled, so the user can see what it touched and
  // undo it wholesale. Only ever lists fields that were BLANK — a typed value
  // is never overwritten, the same rule fillBlankAddressFields applies to
  // geocoded addresses.
  const [ocrFilled, setOcrFilled] = useState<string[]>([]);

  const clearOcr = (): void => {
    for (const label of ocrFilled) {
      if (label === 'amount') setItemAmount('');
      if (label === 'sales tax') setTaxAmount('');
      if (label === 'date') setDateEpoch(todayEpoch());
      if (label === 'vendor') setVendorText('');
      if (label === 'receipt #') setReceiptNumber('');
      if (label === 'address') setNotes('');
    }
    setOcrFilled([]);
  };

  // Latest field values for the async OCR callback, so anything typed WHILE the
  // receipt was being read still wins. Same trick <AddressFields> uses to keep
  // an in-flight geocode from clobbering live input. A ref rather than the
  // setState updater form because deciding "was this blank?" has to record what
  // it filled, and a side effect inside an updater double-fires under
  // StrictMode.
  const liveRef = useRef({
    itemAmount, taxAmount, dateEpoch, vendorText, vendorId, receiptNumber, notes,
  });
  liveRef.current = {
    itemAmount, taxAmount, dateEpoch, vendorText, vendorId, receiptNumber, notes,
  };

  const runOcr = async (file: File): Promise<void> => {
    setOcrFilled([]);
    setOcrPct(0);
    try {
      const { readReceipt } = await import('../utils/receipt-ocr');
      const parsed = await readReceipt(file, setOcrPct);
      if (!parsed) return;

      const cur = liveRef.current;
      const filled: string[] = [];

      if (parsed.total.value !== null && cur.itemAmount.trim() === '') {
        // The form splits item subtotal from sales tax; a receipt's TOTAL is the
        // two together, so back the tax out of it when one was found.
        const tax = parsed.tax.value === null ? 0 : Number(parsed.tax.value);
        setItemAmount(Math.max(0, Number(parsed.total.value) - tax).toFixed(2));
        filled.push('amount');
      }
      if (parsed.tax.value !== null && cur.taxAmount.trim() === '') {
        setTaxAmount(parsed.tax.value);
        filled.push('sales tax');
      }
      // The date field defaults to today, so "untouched" means "still today".
      if (parsed.date.value !== null && cur.dateEpoch === todayEpoch()) {
        setDateEpoch(parsed.date.value);
        filled.push('date');
      }
      if (parsed.vendor.value !== null && cur.vendorText.trim() === '' && cur.vendorId === null) {
        setVendorText(parsed.vendor.value);
        filled.push('vendor');
      }
      if (parsed.receiptNumber.value !== null && cur.receiptNumber.trim() === '') {
        setReceiptNumber(parsed.receiptNumber.value);
        filled.push('receipt #');
      }
      // An expense has no address column, so the vendor's address goes into the
      // notes — searchable, and it travels with the row.
      if (parsed.address.value !== null && cur.notes.trim() === '') {
        setNotes(parsed.address.value);
        filled.push('address');
      }
      setOcrFilled(filled);
    } finally {
      setOcrPct(null);
    }
  };

  const projectsQ = useProjects();
  const assetsQ = useAssets();
  const lookups = useLookups();
  const expenseTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'expense_type');
  const taxCategoryOptions = activeOptionsFor(lookups.data?.lookup_options, 'tax_category');
  const assets = assetsQ.data?.assets ?? [];

  // Live total preview: item subtotal + sales tax. The server stores
  // this as the `amount` column; the API also receives tax_amount as
  // its own field so we can break it back out for reporting later.
  const itemNumber = Number(itemAmount);
  const taxNumber = Number(taxAmount);
  const itemValid = itemAmount.trim() !== '' && Number.isFinite(itemNumber) && itemNumber > 0;
  const taxValid = taxAmount.trim() === '' || (Number.isFinite(taxNumber) && taxNumber >= 0);
  const totalPreview = itemValid && taxValid
    ? (itemNumber + (Number.isFinite(taxNumber) ? taxNumber : 0))
    : null;

  if (projectsQ.isPending) return <p className="nex-status">Loading projects…</p>;
  if (projectsQ.error) {
    return <p className="nex-status nex-error">Could not load projects.</p>;
  }
  const projects = projectsQ.data?.projects ?? [];

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (projectId === 0) {
      setFieldErrors({ project_id: 'Select a project or "General business"' });
      return;
    }
    if (dateEpoch === null) {
      setFieldErrors({ date: 'Pick a date' });
      return;
    }
    if (type.trim().length === 0) {
      setFieldErrors({ type: 'Pick a type' });
      return;
    }
    const projectIdForApi: number | null = projectId === GENERAL_BUSINESS ? null : projectId;

    // NUMERIC(12,2) — toFixed(2) before sending so we never trip the
    // regex (which requires "\d+(\.\d{1,2})?"). Computed before the upload
    // because the receipt's filename carries the total.
    const tax = Number.isFinite(taxNumber) ? taxNumber : 0;
    const taxAmountStr = tax.toFixed(2);
    const totalAmountStr = (itemNumber + tax).toFixed(2);

    try {
      // Upload the receipt photo first (if any) so we can attach its
      // vault path to the new expense atomically. Failure here aborts
      // the create — better to surface the upload error than silently
      // drop the receipt.
      let receiptPath: string | undefined;
      if (receiptFile) {
        setReceiptError(null);
        setReceiptNote(null);
        try {
          // Re-encode anything the API's pdfkit can't embed, then name it
          // before it goes — the vault stores a UUID, so X-Filename is the
          // only place a human name can land.
          const normalized = await normalizeReceiptFile(receiptFile);
          const named = renameFile(normalized, buildReceiptFilename({
            caseNumber: projects.find((p) => p.id === projectIdForApi)?.case_number ?? null,
            dateEpoch,
            receiptNumber: receiptNumber.trim() || null,
            vendor: vendorText.trim() || vendorName,
            type,
            ext: extensionOf(normalized),
          }));
          const resp = await uploadDoc.mutateAsync({
            file: named,
            document_type: 'other',
            convert_to_pdf: true,
            // General-business expenses upload the receipt without a
            // project context. The documents route accepts a missing
            // project_id and files them at the org level.
            ...(projectIdForApi !== null ? { project_id: projectIdForApi } : {}),
          });
          receiptPath = resp.document.file_path;
          if (resp.document.mime_type !== 'application/pdf') {
            setReceiptNote('Kept as the original image — only JPEG and PNG convert to PDF.');
          }
        } catch (err) {
          setReceiptError(err instanceof ApiError ? `Receipt upload failed (${err.status})` : 'Receipt upload failed.');
          return;
        }
      }

      if (!expenseSaved) {
        await create.mutateAsync({
          project_id: projectIdForApi,
          type,
          amount: totalAmountStr,
          tax_amount: taxAmountStr,
          date: dateEpoch,
          // The API snapshots the vendor's display name onto the row from
          // vendor_id. `vendor` is only for the name-only case (a generic
          // category or a one-off) — a vendor_id always overrides it.
          vendor_id: vendorId,
          vendor: vendorId === null ? (vendorText.trim() || undefined) : undefined,
          receipt_number: receiptNumber.trim() || undefined,
          tax_category: taxCategory.trim() || undefined,
          reimbursable: reimbursable || undefined,
          mileage: tracksMileage(type) && mileage.trim() ? mileage.trim() : undefined,
          notes: notes.trim() || undefined,
          receipt_path: receiptPath,
          asset_id: assetId,
          matter_id: projectIdForApi === null ? null : matterId,
        });
        setExpenseSaved(true);
      }

      // If the user asked to repeat it, set up the rule too. The expense
      // above is the first occurrence, so skip_first makes the rule begin
      // generating from the NEXT period (no duplicate of this date).
      const rrule = RECUR_OPTIONS.find((o) => o.value === repeat)?.rrule ?? null;
      if (rrule) {
        try {
          await createRecur.mutateAsync({
            name: recurName.trim() || type,
            project_id: projectIdForApi,
            type,
            amount: totalAmountStr,
            tax_amount: taxAmountStr,
            vendor_id: vendorId,
            vendor: vendorId === null ? (vendorText.trim() || undefined) : undefined,
            tax_category: taxCategory.trim() || undefined,
            reimbursable: reimbursable || undefined,
            notes: notes.trim() || undefined,
            rrule,
            starts_at: dateEpoch,
            skip_first: true,
          });
        } catch (recurErr) {
          const reason = recurErr instanceof ApiError && recurErr.status === 400
            ? ((recurErr.body as { details?: { rrule?: string } } | null)?.details?.rrule ?? 'validation failed')
            : 'try again from the Expenses list';
          setTopError(`Expense saved, but the repeat wasn't set up (${reason}). You can add it from the Expenses list via "repeat".`);
          return; // expenseSaved guards a re-submit from duplicating the expense
        }
      }
      navigate('/expenses');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { details?: { fieldErrors?: Record<string, string[]> } }
          | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
          }
          setFieldErrors(flat);
          setTopError('Please fix the highlighted fields.');
          return;
        }
      }
      if (err instanceof ApiError && err.status === 403) {
        setTopError("You don't have permission to record expenses on this project.");
        return;
      }
      setTopError('Could not record the expense. Please try again.');
    }
  };

  return (
    <section className="nex-section">
      <header className="nex-header">
        <h2>{fromId !== null ? 'Duplicate Expense' : 'New Expense'}</h2>
        <button
          type="button"
          className="btn-text"
          onClick={() => navigate('/expenses')}
        >
          ← Back to expenses
        </button>
      </header>

      {fromId !== null && (
        <p className={`nex-dup-note${sourceQ.error ? ' nex-error' : ''}`} role="status">
          {sourceQ.isPending
            ? 'Loading the expense to copy…'
            : sourceQ.error
              ? apiErrorText(sourceQ.error, 'Could not load the expense to copy.')
              : `Copied from the ${fmtDate(source!.date)} expense${source!.vendor ? ` at ${source!.vendor}` : ''}. `
                + 'The date is today, and the receipt and asset link are not copied — change anything, then save it as a new expense.'}
        </p>
      )}

      <form className="nex-form" onSubmit={onSubmit} noValidate>
        <div className="nex-row">
          <label className="nex-field">
            <span>Project *</span>
            <select
              className="form-input"
              value={projectId}
              onChange={(e) => {
                setProjectId(Number(e.target.value));
                setMatterId(null); // a matter belongs to one project
              }}
              required
            >
              <option value={0}>Select a project…</option>
              <option value={GENERAL_BUSINESS}>— General business (no project) —</option>
              <ProjectOptions projects={projects} />
            </select>
            {fieldErrors['project_id'] && (
              <span className="field-error">{fieldErrors['project_id']}</span>
            )}
          </label>

          <MatterSelect
            className="nex-field"
            projectId={projectId > 0 ? projectId : null}
            value={matterId}
            onChange={setMatterId}
            error={fieldErrors['matter_id']}
          />

          <label className="nex-field">
            <span>Type *</span>
            <select
              className="form-input"
              value={type}
              onChange={(e) => setType(e.target.value)}
              required
            >
              {expenseTypeOptions.map((o) => (
                <option key={o.id} value={o.value}>{o.value}</option>
              ))}
              {/* Preserve legacy value if it's been deleted from the lookup */}
              {type && !expenseTypeOptions.some((o) => o.value === type) && (
                <option value={type}>{type} (legacy)</option>
              )}
            </select>
            {fieldErrors['type'] && (
              <span className="field-error">{fieldErrors['type']}</span>
            )}
          </label>
        </div>

        <div className="nex-row">
          <label className="nex-field">
            <span>Date *</span>
            <DateInput
              value={dateEpoch}
              onChange={setDateEpoch}
              required
            />
          </label>

          <div className="nex-field nex-amount-group">
            <div className="nex-amount-inputs">
              <label className="nex-field">
                <span>Item amount (USD) *</span>
                <MoneyInput
                  value={itemAmount}
                  onChange={setItemAmount}
                  required
                  placeholder="75.00"
                />
                {fieldErrors['amount'] && (
                  <span className="field-error">{fieldErrors['amount']}</span>
                )}
              </label>
              <label className="nex-field">
                <span>Sales tax (USD)</span>
                <MoneyInput
                  value={taxAmount}
                  onChange={setTaxAmount}
                  placeholder="0.00"
                />
                {fieldErrors['tax_amount'] && (
                  <span className="field-error">{fieldErrors['tax_amount']}</span>
                )}
              </label>
            </div>
            <p className="nex-total-preview">
              Total: <strong>${totalPreview !== null ? totalPreview.toFixed(2) : '—'}</strong>
            </p>
          </div>
        </div>

        <div className="nex-row">
          <label className="nex-field">
            <span>Vendor</span>
            <VendorPicker
              value={vendorId}
              onChange={(id, vendor) => {
                setVendorId(id);
                setVendorName(vendor?.name ?? '');
                // Apply the vendor's default tax category — but only if
                // the user hasn't already picked one for this expense.
                if (vendor?.default_tax_category && taxCategory === '') {
                  setTaxCategory(vendor.default_tax_category);
                }
              }}
              allowGeneric
              textValue={vendorText}
              onTextChange={setVendorText}
              canAdd={can('vendor.write')}
              showTaxCategoryHint
            />
          </label>

          <label className="nex-field">
            <span>Receipt #</span>
            <input
              type="text"
              className="form-input"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              maxLength={120}
            />
          </label>
        </div>

        <div className="nex-row">
          <label className="nex-field">
            <span>Tax category</span>
            <select
              className="form-input"
              value={taxCategory}
              onChange={(e) => setTaxCategory(e.target.value)}
            >
              <option value="">— (none) —</option>
              {taxCategoryOptions.map((o) => (
                <option key={o.id} value={o.value}>{o.value}</option>
              ))}
              {taxCategory && !taxCategoryOptions.some((o) => o.value === taxCategory) && (
                <option value={taxCategory}>{taxCategory} (legacy)</option>
              )}
            </select>
          </label>

          {tracksMileage(type) ? (
            <label className="nex-field">
              <span>Mileage (mi)</span>
              <input
                type="text"
                className="form-input"
                value={mileage}
                onChange={(e) => setMileage(e.target.value)}
                placeholder="e.g. 42.5"
                inputMode="decimal"
                pattern="\d+(\.\d{1,2})?"
              />
              {fieldErrors['mileage'] && (
                <span className="field-error">{fieldErrors['mileage']}</span>
              )}
            </label>
          ) : (
            <div /> /* keep grid alignment */
          )}
        </div>

        {can('asset.read') && (
          <label className="nex-field nex-field-full">
            <span>Linked asset (optional)</span>
            <select
              className="form-input"
              value={assetId ?? 0}
              onChange={(e) => {
                const n = Number(e.target.value);
                setAssetId(n > 0 ? n : null);
              }}
              disabled={assetsQ.isPending}
            >
              <option value={0}>— None —</option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {assetOptionLabel(a)}
                </option>
              ))}
            </select>
            <span className="nex-field-hint">
              Tie this purchase or repair to an inventory asset.
            </span>
          </label>
        )}

        <Toggle
          checked={reimbursable}
          onChange={setReimbursable}
          label="Reimbursable to client"
          className="nex-field nex-field-full nex-checkbox"
        />

        <div className="nex-field nex-field-full">
          <span>Receipt photo (optional)</span>
          <FileDrop
            accept="image/*,application/pdf"
            label="Drop a receipt here"
            hint="Image or PDF, up to 25 MB. It's read automatically to fill in the blanks."
            filled={receiptFile?.name ?? null}
            onFiles={(files) => {
              const f = files[0]!;
              setReceiptError(null);
              if (f.size > 25 * 1024 * 1024) {
                setReceiptError('Receipt must be under 25 MB.');
                return;
              }
              setReceiptFile(f);
              void runOcr(f);
            }}
          />
          {ocrPct !== null && (
            <span className="field-meta">Reading receipt… {Math.round(ocrPct * 100)}%</span>
          )}
          {ocrFilled.length > 0 && (
            <span className="field-meta">
              From the receipt: {ocrFilled.join(', ')}.{' '}
              <button type="button" className="nex-link-btn" onClick={clearOcr}>
                Clear
              </button>
            </span>
          )}
          {receiptFile && (
            <span className="field-meta">
              {(receiptFile.size / 1024).toFixed(0)} KB
            </span>
          )}
          {receiptNote && <span className="field-meta">{receiptNote}</span>}
          {receiptError && <span className="field-error">{receiptError}</span>}
        </div>

        <label className="nex-field nex-field-full">
          <span>Notes</span>
          <textarea
            className="form-input nex-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10000}
            placeholder="Optional context — purpose of expense, who was reimbursed, etc."
          />
        </label>

        <div className="nex-row">
          <label className="nex-field">
            <span>Repeat</span>
            <select
              className="form-input"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            >
              {RECUR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          {repeat !== 'none' ? (
            <label className="nex-field">
              <span>Name this repeat</span>
              <input
                type="text"
                className="form-input"
                value={recurName}
                onChange={(e) => setRecurName(e.target.value)}
                placeholder={type || 'e.g. Office rent'}
                maxLength={200}
              />
            </label>
          ) : (
            <div /> /* keep grid alignment */
          )}
        </div>
        {repeat !== 'none' && (
          <p className="nex-repeat-hint">
            Saves this expense for the date above, then creates a repeating rule (
            {RECUR_OPTIONS.find((o) => o.value === repeat)?.label.toLowerCase()}) starting
            from that date. Any periods already due (if you later set an earlier start on the
            rule) are filled in automatically. Manage rules under Expenses → Repeating rules.
          </p>
        )}

        {topError && (
          <p className="nex-error-banner" role="alert">
            {topError}
          </p>
        )}

        <div className="nex-actions">
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => navigate('/expenses')}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-cyan-primary"
            disabled={
              create.isPending ||
              projectId === 0 ||
              !itemValid
            }
          >
            {create.isPending ? 'Saving…' : 'Save expense'}
          </button>
        </div>
      </form>
    </section>
  );
}
